import { useState } from "react";
import { Mail, KeyRound, Loader2, ArrowLeft } from "lucide-react";
import { AreaShell, GreenButton } from "@/components/area/AreaShell";
import { cliente, crmError } from "@/lib/crm";

const inputCls =
  "w-full rounded-xl border border-[#CFD8DC] bg-white px-4 py-3 text-sm text-zr-blue outline-none transition-colors placeholder:text-[#90A4AE] focus:border-zr-green";
const labelCls = "mb-1.5 block text-left text-xs font-semibold uppercase tracking-wide text-zr-grey";

function Notice({ kind, children, testId }) {
  if (!children) return null;
  const cls = kind === "error" ? "border-[#EF9A9A] bg-[#FFEBEE] text-[#C62828]" : "border-[#A5D6A7] bg-[#E8F5E9] text-[#2E7D32]";
  return (
    <p data-testid={testId} className={`mt-4 rounded-xl border px-4 py-3 text-left text-sm font-medium ${cls}`}>
      {children}
    </p>
  );
}

function MagicLinkForm() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [ok, setOk] = useState("");
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setOk(""); setErr("");
    try {
      const d = await cliente.requestMagicLink(email.trim());
      setOk(d.message || "Se la mail è associata a una pratica, riceverai il link entro pochi minuti.");
    } catch (ex) {
      setErr(crmError(ex));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} data-testid="magic-link-form">
      <p className="mb-5 text-sm text-zr-grey">Inserisci l'email della tua pratica: riceverai un link di accesso valido 24 ore, utilizzabile una sola volta.</p>
      <label className={labelCls} htmlFor="ml-email">Email</label>
      <input id="ml-email" data-testid="magic-link-email" type="email" required className={inputCls} placeholder="nome@esempio.it" value={email} onChange={(e) => setEmail(e.target.value)} />
      <GreenButton type="submit" disabled={busy} data-testid="magic-link-submit" className="mt-5 w-full">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />} Inviami il link di accesso
      </GreenButton>
      <Notice kind="ok" testId="magic-link-success">{ok}</Notice>
      <Notice kind="error" testId="magic-link-error">{err}</Notice>
    </form>
  );
}

function OtpForm({ onLogin }) {
  const [pratica, setPratica] = useState("");
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [ok, setOk] = useState("");
  const [err, setErr] = useState("");

  const request = async (e) => {
    e.preventDefault();
    setBusy(true); setOk(""); setErr("");
    try {
      const d = await cliente.requestOtp(pratica.trim(), email.trim());
      setOk(d.message || "Se i dati sono corretti, riceverai un'email con il codice di accesso.");
      setStep(2);
    } catch (ex) {
      setErr(crmError(ex));
    } finally {
      setBusy(false);
    }
  };
  const verify = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const d = await cliente.verifyOtp(pratica.trim(), email.trim(), otp.trim());
      if (!d.session_token) throw new Error("no token");
      onLogin(d.session_token);
    } catch (ex) {
      setErr(crmError(ex, "Codice non valido o scaduto."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={step === 1 ? request : verify} data-testid="otp-form">
      <p className="mb-5 text-sm text-zr-grey">
        Inserisci numero pratica ed email: riceverai un codice a 6 cifre valido 10 minuti (massimo 2 richieste al giorno).
      </p>
      <label className={labelCls} htmlFor="otp-pratica">Numero pratica</label>
      <input id="otp-pratica" data-testid="otp-pratica" required disabled={step === 2} className={inputCls} placeholder="es. SA-000011" value={pratica} onChange={(e) => setPratica(e.target.value)} />
      <label className={`${labelCls} mt-4`} htmlFor="otp-email">Email</label>
      <input id="otp-email" data-testid="otp-email" type="email" required disabled={step === 2} className={inputCls} placeholder="nome@esempio.it" value={email} onChange={(e) => setEmail(e.target.value)} />
      {step === 2 && (
        <>
          <label className={`${labelCls} mt-4`} htmlFor="otp-code">Codice ricevuto via email</label>
          <input id="otp-code" data-testid="otp-code" required inputMode="numeric" pattern="[0-9]{6}" maxLength={6} className={`${inputCls} text-center text-2xl font-bold tracking-[0.5em]`} placeholder="••••••" value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))} />
        </>
      )}
      <GreenButton type="submit" disabled={busy} data-testid="otp-submit" className="mt-5 w-full">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />}
        {step === 1 ? "Inviami il codice" : "Accedi"}
      </GreenButton>
      {step === 2 && (
        <button type="button" data-testid="otp-back" onClick={() => { setStep(1); setOtp(""); setOk(""); setErr(""); }} className="mt-3 text-xs font-semibold text-zr-grey underline-offset-2 hover:underline">
          Modifica i dati inseriti
        </button>
      )}
      <Notice kind="ok" testId="otp-success">{ok}</Notice>
      <Notice kind="error" testId="otp-error">{err}</Notice>
    </form>
  );
}

export const ClienteAccess = ({ onLogin, initialError }) => {
  const [mode, setMode] = useState("magic");
  const tab = (id, label, Icon) => (
    <button
      type="button"
      data-testid={`access-tab-${id}`}
      onClick={() => setMode(id)}
      className={`flex flex-1 items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold transition-colors ${
        mode === id ? "bg-zr-blue text-white" : "bg-zr-light text-zr-blue hover:bg-[#D6EBFB]"
      }`}
    >
      <Icon className="h-4 w-4" /> {label}
    </button>
  );
  return (
    <AreaShell testId="area-cliente-access" title="Area Clienti" subtitle="Accedi con Magic Link o codice OTP">
      <div className="mb-6 flex gap-2">
        {tab("magic", "Magic Link", Mail)}
        {tab("otp", "Codice OTP", KeyRound)}
      </div>
      {initialError && <Notice kind="error" testId="magic-link-consume-error">{initialError}</Notice>}
      <div className={initialError ? "mt-4" : ""}>{mode === "magic" ? <MagicLinkForm /> : <OtpForm onLogin={onLogin} />}</div>
      <a href="/" data-testid="access-back-home" className="mt-8 inline-flex items-center gap-1.5 text-xs font-semibold text-zr-grey hover:text-zr-blue">
        <ArrowLeft className="h-3.5 w-3.5" /> Torna alla home
      </a>
    </AreaShell>
  );
};
